import Image from 'next/image'
import { useRouter } from 'next/router'

import clsx from 'clsx'

import { Link } from '@/components/CustomLink'
import { FullScreenLoading } from '@/components/FullScreenLoading'

import { Pagination } from '../../Pagination'
import { useThemesList } from './useThemesList'

type Props = {
	className?: string
}

const DEFAULT_LIMIT = 12

export const ThemesList = ({ className }: Props) => {
	const { locale = 'en-US' } = useRouter()

	const currency: Record<string, string> = {
		'en-US': 'USD',
		'en-AU': 'AUD',
		'en-GB': 'GBP'
	}

	const { results, isLoading } = useThemesList()

	const items = results?.hits
	const total = results?.nbHits ?? 0

	let themesListResults
	if (!isLoading && Array.isArray(items) && items.length === 0) {
		themesListResults = (
			<p className="italic text-lg">
				There are no exact matches for your search criteria at this time.&nbsp;
				<span className="font-semibold">Below are some of our most popular themes.</span>
			</p>
		)
	} else if (!isLoading && Array.isArray(items)) {
		themesListResults = (
			<>
				<div className="z-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
					{items.map((theme) => {
						if (!theme) return null
						const price = new Intl.NumberFormat(locale, {
							style: 'currency',
							currency: currency[locale]
						}).format(Number(theme.pricePlainText) / 100)

						return (
							<div
								data-testid="theme-card"
								key={theme.objectID}
								className={`min-w-[250px] lg:min-w-[200px] lg:min-w-auto lg:mb-6 bg-white shadow-lg rounded-xl relative overflow-hidden `}
							>
								<Link href={`/theme/${theme.slug}/`}>
									{/* Image */}
									<div className="h-[250px] w-full relative">
										<Image
											src={theme.largeThumbnail!}
											alt={theme.title!}
											fill
											objectFit="cover"
											objectPosition="top"
										/>
									</div>

									{/* Text Content */}
									<div className="p-7" style={{ boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)' }}>
										<h2 className="mb-0.5 text-bc-black font-medium">{theme.title}</h2>

										{theme.related?.length! > 0 && (
											<p className="text-gray-700 line-clamp-1 mb-2">
												Includes {theme.related?.length} style
												{theme.related?.length! > 1 && 's'}
											</p>
										)}

										<h2 className="mb-0.5 text-bc-black font-medium">Pricing</h2>
										<p className="text-gray-700 line-clamp-3">
											{parseFloat(theme.pricePlainText!) > 0 ? price : 'Free'}
										</p>
									</div>
								</Link>
							</div>
						)
					})}
				</div>
				<div className="flex float-left pt-16">
					<Pagination limit={DEFAULT_LIMIT} total={total} />
				</div>
			</>
		)
	} else {
		return null
	}

	return (
		<section className={clsx('w-full', className)}>
			<div className="container relative">
				{<FullScreenLoading show={isLoading} />}

				<div className={`transition-opacity${isLoading ? ' opacity-50' : ''}`}>
					{themesListResults}
				</div>
			</div>
		</section>
	)
}
